import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Calendar,
  MapPin,
  Clock,
  Users,
  Heart,
  CheckCircle,
  ExternalLink,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Camera,
  Instagram,
  X,
} from "lucide-react";
import PublicNav from "@/components/PublicNav";
import { useContent } from "@/context/ContentContext";
import { useLocalize } from "@/lib/localize";
import { useT } from "@/lib/translations";
import { useLanguage } from "@/context/LanguageContext";

const formatDate = (iso, locale) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(locale === "en" ? "en-US" : "tr-TR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
};

const photoSrc = (url) =>
  url?.startsWith("http") ? url : `${API.replace(/\/api$/, "")}${url}`;

export default function EventsPage() {
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [allPhotos, setAllPhotos] = useState([]);
  // Yüklenemeyen kareler duvarda kırık ikon bırakmasın → listeden düşürülür.
  const [brokenPhotos, setBrokenPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // {photos: [...], index: 0}
  const { t } = useContent();
  const L = useT();

  useEffect(() => {
    let cancelled = false;
    // List (upcoming + past)
    axios
      .get(`${API}/public/adoption-events`)
      .then((r) => {
        if (cancelled) return;
        setUpcoming(r.data.upcoming || []);
        setPast(r.data.past || []);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    // Photo wall (independent — never blocks main page)
    axios
      .get(`${API}/public/adoption-events/photos?limit=48`)
      .then((r) => !cancelled && setAllPhotos(r.data.photos || []))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Close lightbox on Escape, navigate with arrow keys
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight")
        setLightbox((lb) =>
          lb ? { ...lb, index: (lb.index + 1) % lb.photos.length } : lb
        );
      if (e.key === "ArrowLeft")
        setLightbox((lb) =>
          lb
            ? { ...lb, index: (lb.index - 1 + lb.photos.length) % lb.photos.length }
            : lb
        );
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  // Open lightbox for the clicked photo, scoped to its event's photos.
  const photos = allPhotos.filter((p) => !brokenPhotos.includes(p.url));
  const handlePhotoClick = (photo) => {
    const sameEvent = photos.filter((p) => p.event_id === photo.event_id);
    const idx = sameEvent.findIndex((p) => p.url === photo.url);
    setLightbox({ photos: sameEvent, index: idx < 0 ? 0 : idx });
  };

  return (
    <div className="min-h-screen bg-background" data-testid="events-page">
      {/* Nav */}
      <PublicNav activeKey="etkinlikler" />

      {/* Hero */}
      <section className="bg-gradient-to-br from-amber-50 via-rose-50 to-background border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full mb-5">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                {L("Pativista Sahiplendirme Günü · KAHU destekli")}
              </span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-6xl tracking-tight mb-4">
              {L("Kuğulu Park'ta")} <span className="text-primary">{L("Buluşalım")}</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {L("Her hafta bir avuç gönüllü, onlarca can ve sevgi dolu bir park. Gel tanış — çocuklar köpeklerle oynasın, sen yeni dostunla tanış, biz birlikte kimsesiz tek bir can kalmayana dek mücadele verelim 💪")}
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 space-y-16">
        {loading ? (
          <div className="text-center text-muted-foreground py-12">{L("Yükleniyor…")}</div>
        ) : (
          <>
            {/* Upcoming */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <Calendar className="w-6 h-6 text-primary" strokeWidth={1.5} />
                <h2 className="font-heading text-3xl">{L("Yaklaşan Etkinlikler")}</h2>
              </div>
              {upcoming.length === 0 ? (
                <Card className="border-dashed bg-gradient-to-br from-rose-50/60 via-amber-50/40 to-background" data-testid="events-empty-instagram">
                  <CardContent className="p-8 md:p-10">
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-xl md:text-2xl mb-2">
                          {L("Bir sonraki buluşma Instagram'da duyurulur")}
                        </h3>
                        <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                          {L("Pativista, sahiplendirme günlerini kendi Instagram sayfasından paylaşıyor. Tarih netleştiği anda ilk oradan duyurulur — takipte kal, biz de burada canları ve sonuçları paylaşmaya devam edeceğiz.")}
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row md:flex-col gap-3 shrink-0">
                        <a
                          href={t("legal.instagram_url", "https://www.instagram.com/pativista/")}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-pill inline-flex items-center justify-center gap-2 px-5 py-3 bg-primary text-primary-foreground font-medium hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/30 transition-all"
                          data-testid="events-empty-instagram-btn"
                        >
                          <Instagram className="w-4 h-4" strokeWidth={1.75} />
                          {L("Pativista'yı Instagram'da Takip Et")}
                        </a>
                        <Link
                          to="/canlar"
                          className="btn-pill inline-flex items-center justify-center gap-2 px-5 py-3 border border-border bg-background font-medium hover:bg-muted/50 transition-colors"
                          data-testid="events-empty-dogs-btn"
                        >
                          {L("Yuva Bekleyen Canları Gör")}
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {upcoming.map((e) => (
                    <UpcomingCard key={e.event_id} event={e} />
                  ))}
                </div>
              )}
            </section>

            {/* Past */}
            {past.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <CheckCircle className="w-6 h-6 text-emerald-600" strokeWidth={1.5} />
                  <h2 className="font-heading text-3xl">{L("Geçmiş Etkinlikler")}</h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {past.map((e) => (
                    <PastCard key={e.event_id} event={e} />
                  ))}
                </div>
              </section>
            )}

            {/* Photo wall — geçmiş etkinliklerden kareler */}
            {photos.length > 0 && (
              <section>
                <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
                  <div className="flex items-center gap-3">
                    <Camera className="w-6 h-6 text-primary" strokeWidth={1.5} />
                    <h2 className="font-heading text-3xl">{L("Geçmiş Etkinliklerden Kareler")}</h2>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {photos.length} {L("an")}
                  </Badge>
                </div>
                <p className="text-muted-foreground mb-8 max-w-2xl">
                  {L("Her Pazar bir araya geldiğimiz Kuğulu Park'tan kareler. Üstüne gel — o anın hangi etkinlikten olduğunu gör; tıkla — büyüt.")}
                </p>
                <PhotoWall
                  photos={photos}
                  onOpen={handlePhotoClick}
                  onBroken={(url) => setBrokenPhotos((prev) => (prev.includes(url) ? prev : [...prev, url]))}
                />
              </section>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="py-12 bg-card border-t border-border/50 mt-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <p className="text-sm text-muted-foreground">
            {L("Pativista ile birlikte — KAHU dijital destek altyapısı")}
          </p>
        </div>
      </footer>

      {lightbox && (
        <Lightbox
          photos={lightbox.photos}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onNavigate={(nextIndex) =>
            setLightbox((lb) => (lb ? { ...lb, index: nextIndex } : lb))
          }
        />
      )}
    </div>
  );
}

function UpcomingCard({ event }) {
  const navigate = useNavigate();
  const Lo = useLocalize();
  const L = useT();
  const { locale } = useLanguage();
  const dogCount = event.featured_dogs_count || 0;
  const goDetail = () => navigate(`/etkinlikler/${event.event_id}`);
  return (
    <div onClick={goDetail} role="link" tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && goDetail()}
      className="cursor-pointer">
      <Card className="card-hover overflow-hidden bg-card border border-border/50 shadow-xl shadow-black/5 h-full">
        <div className="relative bg-gradient-to-br from-primary/10 to-amber-100/40 p-6">
          <Badge className="bg-primary text-primary-foreground mb-3">
            {event.status === "active" ? L("Bugün!") : L("Yaklaşan")}
          </Badge>
          <h3 className="font-heading text-2xl mb-1">{Lo(event, "title")}</h3>
          {Lo(event, "subtitle") && (
            <p className="text-xs text-primary font-semibold uppercase tracking-wider">
              {Lo(event, "subtitle")}
            </p>
          )}
        </div>
        <CardContent className="p-6 space-y-3">
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">{formatDate(event.event_date, locale)}</p>
              <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                {event.start_time}–{event.end_time}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">{Lo(event, "location_name")}</p>
              <p className="text-sm text-muted-foreground">{Lo(event, "location_address")}</p>
            </div>
          </div>
          {dogCount > 0 && (
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-primary flex-shrink-0" />
              <p className="text-sm">
                <span className="font-semibold">{dogCount} {L("can")}</span> {L("seni bekliyor")}
              </p>
            </div>
          )}
          {Lo(event, "description") && (
            <p className="text-sm text-muted-foreground pt-2 border-t">{Lo(event, "description")}</p>
          )}
          <div className="flex items-center justify-between pt-3 border-t">
            {event.map_url && (
              <a
                href={event.map_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-sm text-primary hover:underline inline-flex items-center gap-1"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                {L("Haritada Aç")}
              </a>
            )}
            <span className="text-sm text-primary font-medium inline-flex items-center gap-1">
              {L("Detay")} <ChevronRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PastCard({ event }) {
  const Lo = useLocalize();
  const L = useT();
  const { locale } = useLanguage();
  const [photoBroken, setPhotoBroken] = useState(false);
  const r = event.recap || {};
  const firstPhoto = (r.photos || [])[0];
  return (
    <Link to={`/etkinlikler/${event.event_id}`}>
      <Card className="card-hover overflow-hidden bg-card border border-border/50 shadow-lg h-full">
        {firstPhoto && !photoBroken ? (
          <div className="aspect-[16/10] overflow-hidden bg-muted">
            <img
              src={photoSrc(firstPhoto)}
              alt={Lo(event, "title")}
              onError={() => setPhotoBroken(true)}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="aspect-[16/10] bg-gradient-to-br from-muted to-muted/40 flex items-center justify-center">
            <Calendar className="w-10 h-10 text-muted-foreground/50" />
          </div>
        )}
        <CardContent className="p-5">
          <p className="text-xs text-muted-foreground mb-1">{formatDate(event.event_date, locale)}</p>
          <h3 className="font-heading text-lg mb-2">{Lo(event, "location_name")}</h3>
          {r.adoptions_count > 0 && (
            <p className="text-sm text-emerald-700 inline-flex items-center gap-1.5">
              <Heart className="w-4 h-4" />
              <span className="font-semibold">{r.adoptions_count}</span> {L("can yuva buldu")}
            </p>
          )}
          {(r.visitors_estimated > 0 || r.donations_total > 0) && (
            <p className="text-xs text-muted-foreground mt-1">
              {r.visitors_estimated > 0 && `${r.visitors_estimated} ${L("ziyaretçi")}`}
              {r.visitors_estimated > 0 && r.donations_total > 0 && " · "}
              {r.donations_total > 0 &&
                `${Number(r.donations_total).toLocaleString(locale === "en" ? "en-US" : "tr-TR")} ${L("TL bağış")}`}
            </p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}


function PhotoWall({ photos, onOpen, onBroken }) {
  const { locale } = useLanguage();
  const Lo = useLocalize();
  // Masonry via CSS columns — varies row heights naturally for an organic, social-wall feel.
  return (
    <div
      className="[column-fill:_balance] columns-2 sm:columns-3 lg:columns-4 gap-3 space-y-3"
      data-testid="event-photo-wall"
    >
      {photos.map((p, i) => {
        const title = Lo(p, "event_title");
        const location = Lo(p, "location_name");
        const dateLabel = (() => {
          try {
            return new Date(p.event_date).toLocaleDateString(locale === "en" ? "en-US" : "tr-TR", {
              day: "numeric",
              month: "long",
              year: "numeric",
            });
          } catch {
            return p.event_date;
          }
        })();
        return (
          <button
            key={`${p.url}-${i}`}
            type="button"
            onClick={() => onOpen(p)}
            className="group relative w-full break-inside-avoid rounded-2xl overflow-hidden bg-muted block focus:outline-none focus:ring-2 focus:ring-primary cursor-zoom-in"
            aria-label={`${title} — ${dateLabel}`}
            data-testid={`photo-wall-item-${i}`}
          >
            <img
              src={photoSrc(p.url)}
              alt={`${title} ${dateLabel}`}
              loading="lazy"
              onError={() => onBroken?.(p.url)}
              className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
            />
            {/* Overlay caption — appears on hover */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <p className="text-xs uppercase tracking-wider text-amber-200 font-semibold mb-0.5">
                {dateLabel}
              </p>
              <p className="text-sm text-white font-medium leading-tight line-clamp-2">
                {title}
              </p>
              <p className="text-xs text-white/80 mt-0.5 inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {location}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function Lightbox({ photos, index, onClose, onNavigate }) {
  const Lo = useLocalize();
  const { locale } = useLanguage();
  const photo = photos[index];
  const total = photos.length;
  const goPrev = (e) => {
    e?.stopPropagation();
    onNavigate((index - 1 + total) % total);
  };
  const goNext = (e) => {
    e?.stopPropagation();
    onNavigate((index + 1) % total);
  };
  // Touch swipe handlers
  const touchStart = useRef(null);
  const onTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    touchStart.current = null;
    if (Math.abs(dx) < 40 || total <= 1) return;
    if (dx < 0) goNext();
    else goPrev();
  };
  const dateLabel = (() => {
    try {
      return new Date(photo.event_date).toLocaleDateString(locale === "en" ? "en-US" : "tr-TR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return photo.event_date;
    }
  })();

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10 animate-in fade-in"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="photo-lightbox"
    >
      {/* Close */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
        aria-label="Kapat"
        data-testid="lightbox-close"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Counter */}
      {total > 1 && (
        <div
          className="absolute top-5 left-5 px-3 py-1.5 rounded-full bg-white/10 text-white text-xs font-medium z-10"
          data-testid="lightbox-counter"
        >
          {index + 1} / {total}
        </div>
      )}

      {/* Prev */}
      {total > 1 && (
        <button
          type="button"
          onClick={goPrev}
          className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all hover:scale-110 z-10"
          aria-label="Önceki"
          data-testid="lightbox-prev"
        >
          <ChevronLeft className="w-6 h-6 md:w-7 md:h-7" />
        </button>
      )}

      {/* Next */}
      {total > 1 && (
        <button
          type="button"
          onClick={goNext}
          className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all hover:scale-110 z-10"
          aria-label="Sonraki"
          data-testid="lightbox-next"
        >
          <ChevronRight className="w-6 h-6 md:w-7 md:h-7" />
        </button>
      )}

      <div
        className="max-w-6xl w-full flex flex-col items-center gap-5"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          key={photo.url}
          src={photoSrc(photo.url)}
          alt={Lo(photo, "event_title")}
          className="max-h-[72vh] w-auto max-w-full rounded-xl shadow-2xl object-contain animate-in fade-in duration-200"
        />

        {/* Thumbnail strip (only when multiple) */}
        {total > 1 && (
          <div className="flex gap-2 overflow-x-auto max-w-full px-2 pb-1">
            {photos.map((p, i) => (
              <button
                key={p.url}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigate(i);
                }}
                className={`flex-shrink-0 w-14 h-14 rounded-md overflow-hidden transition-all ${
                  i === index
                    ? "ring-2 ring-amber-300 opacity-100"
                    : "opacity-50 hover:opacity-80"
                }`}
              >
                <img src={photoSrc(p.url)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        <div className="text-center text-white">
          <p className="text-xs uppercase tracking-wider text-amber-300 font-semibold mb-1">
            {dateLabel}
          </p>
          <p className="font-heading text-xl md:text-2xl mb-1">{Lo(photo, "event_title")}</p>
          <p className="text-sm text-white/70 inline-flex items-center gap-1.5 justify-center">
            <MapPin className="w-3.5 h-3.5" />
            {Lo(photo, "location_name")}
          </p>
          <Link
            to={`/etkinlikler/${photo.event_id}`}
            className="inline-flex items-center gap-1.5 mt-4 px-5 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
          >
            Etkinliği Gör <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
